import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Collapse } from '@/shared/components/Collapse'
import {
  useGetMachineryDirectiveCompliance,
  useListStandardApplicability,
  type StandardApplicability,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useListUserStandards } from '@/shared/hooks/api/userStandards'
import { type ExtractedRiskReductionMeasure } from '@/shared/hooks/api/standards'
import type { UserStandard } from '@/shared/hooks/api/userStandards'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  resolveLocalizedRiskReductionMeasureField,
} from '@/shared/lib/standardEntityLocalizations'
import type { ComplianceDocumentsLocaleKey } from '@artem-lex/compliance-documents'
import { StandardDataStatusBadge } from '@/components/StandardDataStatusBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Item,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'

type StandardsRrmPreviewItem = {
  id: string
  index: number
  standardReference: string
  name: string
  type: 'ISD' | 'SCP' | 'PPE' | 'OI'
  description: string
  clauseReference?: string
  dependsOnControlSystem: boolean | null
}

function ExpandableItemDescription({
  description,
  showMoreLabel,
  showLessLabel,
}: {
  description: string
  showMoreLabel: string
  showLessLabel: string
}) {
  const [expanded, setExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const descriptionRef = useRef<HTMLParagraphElement>(null)

  useLayoutEffect(() => {
    const element = descriptionRef.current
    if (element == null || expanded) {
      return
    }
    const updateIsClamped = () => {
      setIsClamped(element.scrollHeight > element.clientHeight + 1)
    }
    updateIsClamped()
    const observer = new ResizeObserver(updateIsClamped)
    observer.observe(element)
    return () => observer.disconnect()
  }, [description, expanded])

  const showToggle = expanded || isClamped

  return (
    <div>
      <p
        ref={descriptionRef}
        data-slot='item-description'
        className={cn(
          'text-left text-sm leading-normal font-normal text-muted-foreground',
          expanded ? 'line-clamp-none' : 'line-clamp-2'
        )}
      >
        {description}
      </p>
      {showToggle && (
        <button
          type='button'
          className='mt-1 text-sm text-primary hover:underline'
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            setExpanded((previous) => !previous)
          }}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  )
}

function applicabilityNeedsDataBadge(sa: StandardApplicability): boolean {
  const hasData =
    sa.hasSignificantHazards === true ||
    sa.hasInformationForUse === true ||
    sa.hasRiskReductionMeasures === true
  return !hasData
}

function groupByStandard(
  items: StandardsRrmPreviewItem[]
): Map<string, StandardsRrmPreviewItem[]> {
  const map = new Map<string, StandardsRrmPreviewItem[]>()
  for (const item of items) {
    const ref = item.standardReference
    const list = map.get(ref) ?? []
    list.push(item)
    map.set(ref, list)
  }
  return map
}

function buildStandardsRrmPreviewItems(
  standards: UserStandard[],
  referencesOrder: string[],
  displayLocale?: ComplianceDocumentsLocaleKey
): StandardsRrmPreviewItem[] {
  const byRef = new Map(standards.map((standard) => [standard.reference, standard]))
  const items: StandardsRrmPreviewItem[] = []
  let index = 0
  for (const ref of referencesOrder) {
    const standard = byRef.get(ref)
    const rawMeasures = standard?.riskReductionMeasures?.measures
    if (!rawMeasures || !Array.isArray(rawMeasures)) {
      continue
    }

    for (const measure of rawMeasures) {
      const extracted = measure as ExtractedRiskReductionMeasure
      const id =
        extracted.id != null && String(extracted.id).trim() !== ''
          ? String(extracted.id).trim()
          : ''
      const nameRaw =
        displayLocale != null
          ? resolveLocalizedRiskReductionMeasureField(
              extracted,
              displayLocale,
              'name'
            )
          : extracted.name
      const descriptionRaw =
        displayLocale != null
          ? resolveLocalizedRiskReductionMeasureField(
              extracted,
              displayLocale,
              'description'
            )
          : extracted.description
      const name =
        nameRaw != null && String(nameRaw).trim() !== ''
          ? String(nameRaw).trim()
          : ''
      const description =
        descriptionRaw != null && String(descriptionRaw).trim() !== ''
          ? String(descriptionRaw).trim()
          : ''

      if (id === '' || name === '' || description === '') {
        continue
      }

      const clauseReferenceRaw = extracted.clauseReference
      const clauseReference =
        clauseReferenceRaw != null && String(clauseReferenceRaw).trim() !== ''
          ? String(clauseReferenceRaw).trim()
          : undefined

      items.push({
        id,
        index,
        standardReference: ref,
        name,
        description,
        clauseReference,
        type: extracted.type,
        dependsOnControlSystem:
          extracted.type === 'SCP' &&
          typeof extracted.dependsOnControlSystem === 'boolean'
            ? extracted.dependsOnControlSystem
            : null,
      })
      index += 1
    }
  }
  return items
}

type GenerateRrmsFromStandardsModalProps = {
  isOpen: boolean
  onClose: () => void
  complianceId: string
  onGenerate: (selectedMeasureIds: string[]) => void
  isGenerating?: boolean
  generatedStandardExtractedMeasureIds?: ReadonlySet<string>
}

export function GenerateRrmsFromStandardsModal({
  isOpen,
  onClose,
  complianceId,
  onGenerate,
  isGenerating = false,
  generatedStandardExtractedMeasureIds = new Set(),
}: GenerateRrmsFromStandardsModalProps) {
  const { data: compliance } = useGetMachineryDirectiveCompliance(
    isOpen ? complianceId : undefined
  )
  const displayLocale = compliance?.contentLanguage
  const {
    data: applicability,
    isLoading: isLoadingApplicability,
    error: applicabilityError,
  } = useListStandardApplicability(complianceId)

  const acceptedApplicabilityWithUserStandard = useMemo(
    () =>
      applicability?.filter(
        (applicableStandard) =>
          applicableStandard.isAccepted === true &&
          applicableStandard.userStandardId != null
      ) ?? [],
    [applicability]
  )

  const { data: userStandardsList = [], isLoading: isLoadingUserStandards } =
    useListUserStandards({
      enabled: isOpen && acceptedApplicabilityWithUserStandard.length > 0,
    })

  const userStandardsById = useMemo(() => {
    const map = new Map(
      userStandardsList
        .filter((userStandard) =>
          acceptedApplicabilityWithUserStandard.some(
            (applicableStandard) =>
              applicableStandard.userStandardId === userStandard.id
          )
        )
        .map((userStandard) => [userStandard.id, userStandard])
    )
    return map
  }, [userStandardsList, acceptedApplicabilityWithUserStandard])

  const standardsForRrms = useMemo((): UserStandard[] => {
    return acceptedApplicabilityWithUserStandard
      .map((applicableStandard) => {
        const userStandard =
          applicableStandard.userStandardId != null
            ? userStandardsById.get(applicableStandard.userStandardId)
            : undefined
        return userStandard ?? null
      })
      .filter((standard): standard is UserStandard => standard != null)
  }, [acceptedApplicabilityWithUserStandard, userStandardsById])

  const items = useMemo(
    () =>
      buildStandardsRrmPreviewItems(
        standardsForRrms,
        acceptedApplicabilityWithUserStandard.map(
          (applicableStandard) => applicableStandard.reference
        ),
        displayLocale
      ),
    [standardsForRrms, acceptedApplicabilityWithUserStandard, displayLocale]
  )
  const grouped = useMemo(() => groupByStandard(items), [items])

  const isLoading =
    isLoadingApplicability ||
    (acceptedApplicabilityWithUserStandard.length > 0 && isLoadingUserStandards)
  const error = applicabilityError

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className='flex max-h-[85vh] flex-col gap-4 sm:max-w-2xl'
        showCloseButton={!isGenerating}
      >
        {isOpen ? (
          <GenerateRrmsFromStandardsModalBody
            grouped={grouped}
            items={items}
            acceptedApplicability={acceptedApplicabilityWithUserStandard}
            isLoading={isLoading}
            error={error}
            isGenerating={isGenerating}
            onGenerate={onGenerate}
            onClose={onClose}
            generatedStandardExtractedMeasureIds={
              generatedStandardExtractedMeasureIds
            }
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

type ModalBodyProps = {
  grouped: Map<string, StandardsRrmPreviewItem[]>
  items: StandardsRrmPreviewItem[]
  acceptedApplicability: StandardApplicability[]
  isLoading: boolean
  error: Error | null
  isGenerating: boolean
  onGenerate: (selectedMeasureIds: string[]) => void
  onClose: () => void
  generatedStandardExtractedMeasureIds: ReadonlySet<string>
}

function selectableIdsForGroup(
  groupItems: StandardsRrmPreviewItem[],
  generatedStandardExtractedMeasureIds: ReadonlySet<string>
) {
  return groupItems
    .map((item) => item.id)
    .filter(
      (id) => id !== '' && !generatedStandardExtractedMeasureIds.has(String(id))
    )
}

function GenerateRrmsFromStandardsModalBody({
  grouped,
  items,
  acceptedApplicability,
  isLoading,
  error,
  isGenerating,
  onGenerate,
  onClose,
  generatedStandardExtractedMeasureIds,
}: ModalBodyProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [collapsedStandards, setCollapsedStandards] = useState<Set<string>>(
    new Set()
  )

  const toggleCollapsed = useCallback((standardRef: string) => {
    setCollapsedStandards((prev) => {
      const next = new Set(prev)
      if (next.has(standardRef)) {
        next.delete(standardRef)
      } else {
        next.add(standardRef)
      }
      return next
    })
  }, [])

  const toggleId = useCallback(
    (id: string) => {
      if (id === '' || generatedStandardExtractedMeasureIds.has(id)) {
        return
      }
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return next
      })
    },
    [generatedStandardExtractedMeasureIds]
  )

  const toggleStandard = useCallback(
    (standardRef: string) => {
      const groupItems = grouped.get(standardRef) ?? []
      const ids = selectableIdsForGroup(
        groupItems,
        generatedStandardExtractedMeasureIds
      )
      if (ids.length === 0) {
        return
      }
      const allSelected = ids.every((id) => selectedIds.has(id))
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (allSelected) {
          ids.forEach((id) => next.delete(id))
        } else {
          ids.forEach((id) => next.add(id))
        }
        return next
      })
    },
    [grouped, selectedIds, generatedStandardExtractedMeasureIds]
  )

  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds])
  const canGenerate = selectedList.length > 0 && !isGenerating

  const handleGenerate = useCallback(() => {
    if (!canGenerate) {
      return
    }
    onGenerate(selectedList)
    onClose()
  }, [canGenerate, selectedList, onGenerate, onClose])

  const listRows = useMemo(() => {
    const rows: Array<{
      key: string
      sa: StandardApplicability
      groupItems: StandardsRrmPreviewItem[]
    }> = []
    for (const sa of acceptedApplicability) {
      const groupItems = grouped.get(sa.reference) ?? []
      if (groupItems.length > 0) {
        rows.push({ key: sa.id, sa, groupItems })
        continue
      }
      rows.push({ key: sa.id, sa, groupItems: [] })
    }
    return rows
  }, [acceptedApplicability, grouped])

  const hasAnyRrms = items.length > 0

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {t('riskReductionMeasuresLibraryPage.generateFromStandardsModal.title')}
        </DialogTitle>
      </DialogHeader>

      <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
        {isLoading && (
          <div className='flex justify-center py-8'>
            <Skeleton className='size-10 rounded-full' />
          </div>
        )}
        {error != null && (
          <p className='text-sm text-destructive'>
            {t('riskReductionMeasuresLibraryPage.generateFromStandardsModal.loadFailed', {
              message: error.message,
            })}
          </p>
        )}
        {!isLoading && error == null && listRows.length === 0 && (
          <p className='py-6 text-center text-muted-foreground'>
            {acceptedApplicability.length === 0
              ? t(
                  'riskReductionMeasuresLibraryPage.generateFromStandardsModal.noRrmsFound'
                )
              : t(
                  'riskReductionMeasuresLibraryPage.generateFromStandardsModal.noRrmsButHasStandards'
                )}
          </p>
        )}
        {!isLoading && error == null && listRows.length > 0 && (
          <div className='space-y-4'>
            {hasAnyRrms && (
              <p className='text-sm text-muted-foreground'>
                {t(
                  'riskReductionMeasuresLibraryPage.generateFromStandardsModal.selectHint'
                )}
              </p>
            )}
            <div className='space-y-4'>
              {listRows.map(({ key, sa, groupItems }) => {
                const standardReference = sa.reference
                if (groupItems.length === 0) {
                  const showDataStatusBadge = applicabilityNeedsDataBadge(sa)
                  return (
                    <Card key={key} className='overflow-hidden'>
                      <div className='flex flex-wrap items-center gap-2 border-b bg-muted/40 px-4 py-2'>
                        <span className='min-w-0 flex-1 text-sm font-medium'>
                          {standardReference}
                        </span>
                        {showDataStatusBadge ? (
                          <StandardDataStatusBadge
                            hasData={
                              sa.hasSignificantHazards === true ||
                              sa.hasInformationForUse === true ||
                              sa.hasRiskReductionMeasures === true
                            }
                            hasPdf={sa.hasFile === true}
                            requireLinkedUserStandard
                            userStandardId={sa.userStandardId}
                          />
                        ) : (
                          <span className='shrink-0 text-xs text-muted-foreground'>
                            {t(
                              'riskReductionMeasuresLibraryPage.generateFromStandardsModal.measuresCount',
                              { count: 0 }
                            )}
                          </span>
                        )}
                      </div>
                    </Card>
                  )
                }
                const selectable = selectableIdsForGroup(
                  groupItems,
                  generatedStandardExtractedMeasureIds
                )
                const allSelected =
                  selectable.length > 0 &&
                  selectable.every((id) => selectedIds.has(id))
                const someSelected = selectable.some((id) => selectedIds.has(id))
                const isExpanded = !collapsedStandards.has(standardReference)
                return (
                  <Card key={key} className='overflow-hidden'>
                    <div className='flex items-center gap-2 border-b bg-muted/40 px-4 py-2'>
                      <button
                        type='button'
                        className='shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground'
                        onClick={() => toggleCollapsed(standardReference)}
                        aria-expanded={isExpanded}
                      >
                        <ChevronDown
                          className={cn('size-5 transition-transform', {
                            'rotate-180': isExpanded,
                          })}
                        />
                      </button>
                      <Checkbox
                        id={`standard-rrm-${standardReference}`}
                        checked={allSelected}
                        disabled={selectable.length === 0}
                        onCheckedChange={() => toggleStandard(standardReference)}
                      />
                      <label
                        htmlFor={`standard-rrm-${standardReference}`}
                        className='min-w-0 flex-1 cursor-pointer text-sm font-medium'
                      >
                        {standardReference}
                      </label>
                      <span className='shrink-0 text-xs text-muted-foreground'>
                        {someSelected
                          ? t(
                              'riskReductionMeasuresLibraryPage.generateFromStandardsModal.selectedCount',
                              {
                                selected: selectable.filter((id) =>
                                  selectedIds.has(id)
                                ).length,
                                total: selectable.length,
                              }
                            )
                          : t(
                              'riskReductionMeasuresLibraryPage.generateFromStandardsModal.measuresCount',
                              { count: groupItems.length }
                            )}
                      </span>
                    </div>
                    <Collapse open={isExpanded} className='pt-2'>
                      <ItemGroup className='px-2 pb-2'>
                        {groupItems.map((item) => {
                          const rowKey = `${standardReference}-${item.index}`
                          const selectableRow = item.id !== ''
                          const alreadyGenerated =
                            selectableRow &&
                            generatedStandardExtractedMeasureIds.has(item.id)
                          const disabledRow = !selectableRow || alreadyGenerated
                          return (
                            <Item
                              key={rowKey}
                              variant='muted'
                              className='hover:bg-muted/60'
                            >
                              <div className='shrink-0 pt-0.5'>
                                <Checkbox
                                  id={`rrm-${rowKey}`}
                                  checked={selectedIds.has(item.id)}
                                  disabled={disabledRow}
                                  onCheckedChange={() => toggleId(item.id)}
                                />
                              </div>
                              <ItemContent>
                                <label
                                  htmlFor={`rrm-${rowKey}`}
                                  className={cn(
                                    'cursor-pointer',
                                    disabledRow && 'cursor-default'
                                  )}
                                >
                                  <ItemTitle className='flex flex-wrap items-start justify-between gap-2'>
                                    <span className='min-w-0'>
                                      {item.clauseReference != null &&
                                        item.clauseReference !== '' && (
                                          <span className='text-muted-foreground'>
                                            {item.clauseReference}.{' '}
                                          </span>
                                        )}
                                      {item.name} ({item.type})
                                    </span>
                                    {alreadyGenerated ? (
                                      <Badge
                                        variant='secondary'
                                        className='shrink-0 font-normal'
                                      >
                                        {t(
                                          'riskReductionMeasuresLibraryPage.generateFromStandardsModal.alreadyGenerated'
                                        )}
                                      </Badge>
                                    ) : null}
                                  </ItemTitle>
                                </label>
                                <ExpandableItemDescription
                                  description={item.description}
                                  showMoreLabel={t(
                                    'riskReductionMeasuresLibraryPage.generateFromStandardsModal.showMore'
                                  )}
                                  showLessLabel={t(
                                    'riskReductionMeasuresLibraryPage.generateFromStandardsModal.showLess'
                                  )}
                                />
                              </ItemContent>
                            </Item>
                          )
                        })}
                      </ItemGroup>
                    </Collapse>
                  </Card>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant='outline' onClick={onClose} disabled={isGenerating}>
          {t('common:actions.cancel')}
        </Button>
        <Button disabled={!canGenerate} onClick={handleGenerate}>
          {t(
            'riskReductionMeasuresLibraryPage.generateFromStandardsModal.generateSelected',
            {
              count: selectedList.length,
            }
          )}
        </Button>
      </DialogFooter>
    </>
  )
}
